import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { CustomFieldType, UNIT_TYPES } from '@ee/common/enums';
import { LayoutItem, LayoutRow } from '@ee/common/models';
import { PlaintiffOptions } from '@ee/common/utils';
import { controlNameFor } from './intake-form-controls';
import { FieldDefinitionView } from './intake-layout.model';
import { LayoutCellComponent } from './layout-cell.component';

/** `$1,200.00` → 1200; blank and non-numeric → 0. Money is typed the way people write it. */
function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value !== 'string' || value === '') return 0;
  const parsed = Number(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

const NO_KEYS: ReadonlySet<string> = new Set();

/**
 * A section's running total: placed DECIMAL fields flagged `include_in_total` add, `subtract_from_total`
 * subtract, and a rent history adds its monthly amounts — replacing a manual Rent Owed once it has rows,
 * so the same arrears are never counted twice. Null when nothing placed in these rows counts.
 *
 * Replaces the SurveyJS `computeClaimsTotal` helper, which hard-coded six keys; this reads the flags.
 */
export function sectionTotal(
  rows: LayoutRow[],
  definitions: Map<string, FieldDefinitionView>,
  form: FormGroup | null,
  hidden: ReadonlySet<string> = NO_KEYS
): number | null {
  // What the rules hide is not being asked, so a value left behind in it does not count.
  const placed = rows
    .flatMap((row) => row.items)
    .map((item) => (item.field_key && !hidden.has(item.field_key) ? definitions.get(item.field_key) : undefined))
    .filter((def): def is FieldDefinitionView => !!def);
  const valueOf = (key: string) => form?.get(key)?.value;
  const histories = placed.filter((def) => def.type === CustomFieldType.RENT_HISTORY && def.include_in_total);
  const historyRows = histories.flatMap((def) =>
    Array.isArray(valueOf(def.key)) ? (valueOf(def.key) as unknown[]) : []
  );

  let counted = histories.length > 0;
  let total = historyRows.reduce<number>((sum, row) => sum + toNumber((row as { amount?: unknown })?.amount), 0);
  placed
    .filter((def) => def.type === CustomFieldType.DECIMAL)
    .forEach((def) => {
      if (def.include_in_total) {
        counted = true;
        if (def.key === 'past_due_rent' && historyRows.length) return;
        total += toNumber(valueOf(def.key));
      } else if (def.subtract_from_total) {
        counted = true;
        total -= toNumber(valueOf(def.key));
      }
    });
  return counted ? Math.round(total * 100) / 100 : null;
}

/**
 * Renders a section's rows at their authored spans.
 *
 * One component for both channels and for the builder's preview — a preview that is a *different*
 * component is a preview that will eventually lie. `preview` only makes it inert; it does not change how
 * anything is laid out.
 *
 * Responsive collapse is a **renderer** rule, not authored state (REQ-5.18): at 1024 and up the spans
 * render as authored, between 768 and 1023 anything below half-width is promoted to half, and below 768
 * it is a single column in authored order. That lives in `_layout.mixin.scss` so the builder preview and
 * the client form cannot disagree.
 */
@Component({
  selector: 'ee-intake-layout-renderer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CurrencyPipe, LayoutCellComponent],
  template: `
    <div class="rendered" [class.is-preview]="preview">
      @for (row of rows; track row.id) {
        <div class="rendered__row">
          @for (item of row.items; track $index) {
            <!--
              A hidden placement is not drawn, rather than drawn and disabled. A greyed-out question a
              client cannot answer and cannot be told the reason for is worse than its absence.
            -->
            @if (!isHidden(item)) {
              <div
                class="rendered__cell"
                [attr.data-span]="item.span"
                [attr.data-col-start]="item.col_start ?? null"
                [attr.data-field-key]="item.field_key ?? null"
                [class.is-disabled]="isDisabled(item)">
                <ee-layout-cell
                  [item]="item"
                  [definition]="definitionFor(item)"
                  [control]="cellControl(item)"
                  [form]="form"
                  [builder]="preview"
                  [liveComposites]="liveComposites"
                  [relaxed]="isRelaxed(item)"
                  [compact]="compact"
                  [unitTypes]="unitTypes"
                  [heading]="heading"
                  [plaintiffOptions]="plaintiffOptions"></ee-layout-cell>
                @if (reasonFor(item); as reason) {
                  <!-- Why this is being asked. Authored on the rule, shown where the question appeared. -->
                  <p class="rendered__reason">{{ reason }}</p>
                }
              </div>
            }
          }
        </div>
      }
      @if (showTotal && total() !== null) {
        <p class="rendered__total">
          <span>Total</span>
          <strong>{{ total() | currency }}</strong>
        </p>
      }
    </div>
  `,
  styleUrl: './intake-layout-renderer.component.scss'
})
export class IntakeLayoutRendererComponent {
  @Input({ required: true }) rows: LayoutRow[] = [];

  @Input() definitions: Map<string, FieldDefinitionView> = new Map();

  /** Renders the same layout, inert. Never a different component — a separate preview eventually lies. */
  @Input() preview = false;

  @Input() form: FormGroup | null = null;

  /**
   * The unit types this form offers, which is the one part of the address block a firm configures.
   * Defaulted to the wizard's full list, because that is what a firm that has narrowed nothing gets.
   */
  @Input() unitTypes: string[] = UNIT_TYPES;

  /** Storage keys and section keys the rules hide. A hidden placement is not drawn. */
  @Input() hidden: ReadonlySet<string> = new Set();

  /** Storage keys the rules disable: drawn, value kept, not editable. */
  @Input() disabled: ReadonlySet<string> = new Set();

  /** Why a question is being asked, by storage key — a firm writes it on the rule. */
  @Input() reasons: ReadonlyMap<string, string> = new Map();

  /** Storage keys an OPTIONAL rule has relaxed: a required placement that need not be answered now. */
  @Input() relaxed: ReadonlySet<string> = new Set();

  /**
   * Whether a composite may run its live behaviour. Off by default: the address block geocodes and looks
   * up counties, and doing that on a canvas that redraws as you drag is a request per frame.
   */
  @Input() liveComposites = false;

  /** The compact density (MODERN) or v1's full-height fields (CLASSIC). Compact unless the host says. */
  @Input() compact = true;

  /** Draw the section's running total under its rows (EE-412 Req 5.7). */
  @Input() showTotal = false;

  /** The heading drawn above these rows (the section's, or the page's when the section's would repeat it). */
  @Input() heading: string | null = null;

  /** Passed through to each cell for the `plaintiff` field (EE-367). */
  @Input() plaintiffOptions: PlaintiffOptions | null = null;

  /**
   * One disabled control per key in preview. Per key, not shared: the uploader and the checkbox group
   * keep state on their control, and one shared control would let every field show another's state.
   */
  private readonly inert = new Map<string, AbstractControl>();

  protected isHidden(item: LayoutItem): boolean {
    return !!item.field_key && this.hidden.has(item.field_key);
  }

  protected isRelaxed(item: LayoutItem): boolean {
    return !!item.field_key && this.relaxed.has(item.field_key);
  }

  protected isDisabled(item: LayoutItem): boolean {
    return !!item.field_key && this.disabled.has(item.field_key);
  }

  protected reasonFor(item: LayoutItem): string {
    return (item.field_key && this.reasons.get(item.field_key)) || '';
  }

  protected definitionFor(item: LayoutItem): FieldDefinitionView | null {
    return item.field_key ? (this.definitions.get(item.field_key) ?? null) : null;
  }

  protected cellControl(item: LayoutItem): AbstractControl | null {
    const key = item.field_key;
    if (!key || item.kind !== 'FIELD') return null;
    if (this.preview) {
      let control = this.inert.get(key);
      if (!control) {
        control = this.definitionFor(item)?.subject_fields?.length
          ? new FormArray([])
          : new FormControl({ value: null, disabled: true });
        control.disable({ emitEvent: false });
        this.inert.set(key, control);
      }
      return control;
    }
    return this.form?.get(controlNameFor(key)) ?? null;
  }

  protected total(): number | null {
    return sectionTotal(this.rows, this.definitions, this.form, this.hidden);
  }
}
